import { ActionIcon, Alert, Box, Button, Group, Loader, NumberInput, SegmentedControl, Select, Stack, Text, Title, Tooltip } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconLock, IconMinus, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { api, DayMetric, SOURCE_NAMES } from '../api';
import { isoDay } from '../format';
import { initialLe, rememberLe } from '../lastLe';
import { defaults, useSession } from '../session';

type Values = Record<string, number | ''>;

/** Показатели дня по отделу. Повторная отправка за тот же день заменяет значения. */
export function MetricsPage() {
  const { me, catalog } = useSession();
  const def = defaults(me);
  const [date, setDate] = useState(isoDay(new Date()));
  const [department, setDepartment] = useState<string | null>(def.departmentId ?? catalog?.departments[0]?.id ?? null);
  // ООО не подставляется молча первым по списку: показатели Леди не должны попасть в Дети.
  const [legalEntity, setLegalEntity] = useState<string | null>(() => initialLe(me, catalog));
  const chooseLe = (id: string) => {
    setLegalEntity(id);
    rememberLe(id);
  };
  const [metrics, setMetrics] = useState<DayMetric[] | null>(null);
  const [values, setValues] = useState<Values>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!department || !legalEntity) {
      setMetrics(null);
      return;
    }
    setMetrics(null);
    setError(null);
    const q = new URLSearchParams({ date, department_id: department, legal_entity_id: legalEntity });
    api
      .get<{ metrics: DayMetric[] }>(`/metrics/day?${q}`)
      .then((r) => {
        setMetrics(r.metrics);
        setValues(Object.fromEntries(r.metrics.map((m) => [m.id, m.value === null ? '' : Number(m.value)])));
      })
      .catch((e) => {
        setMetrics([]);
        setError(e.message);
      });
  }, [date, department, legalEntity]);

  const groups = useMemo(() => {
    const g = new Map<string, DayMetric[]>();
    for (const m of metrics ?? []) g.set(m.group_name, [...(g.get(m.group_name) ?? []), m]);
    return [...g];
  }, [metrics]);

  if (!catalog) return null;
  const myDepartments = me?.roles.some((r) => r.role !== 'dept_head')
    ? catalog.departments
    : catalog.departments.filter((d) => me?.roles.some((r) => r.department_id === d.id));
  const changed = (metrics ?? []).filter((m) => m.editable && (m.value === null ? '' : Number(m.value)) !== values[m.id]);

  async function submit() {
    setBusy(true);
    try {
      await api.put('/metrics/day', {
        date,
        department_id: department,
        legal_entity_id: legalEntity,
        values: (metrics ?? []).filter((m) => m.editable).map((m) => ({ metric_id: m.id, value: values[m.id] === '' ? null : values[m.id] })),
      });
      setMetrics((ms) => (ms ?? []).map((m) => (m.editable ? { ...m, value: values[m.id] === '' ? null : String(values[m.id]), source: 'manual' } : m)));
      notifications.show({ color: 'brand', message: 'Показатели сохранены' });
    } catch (e) {
      notifications.show({ color: 'red', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box maw={640} mx="auto" style={{ minHeight: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
      <Stack gap="sm" p="md" pb={0}>
        <Title order={2}>Показатели дня</Title>
        <DateInput aria-label="День" valueFormat="dddd, D MMMM" value={date} maxDate={new Date()} onChange={(v) => v && setDate(isoDay(v))} size="md" />
        <Group grow gap="sm">
          <Select aria-label="Подразделение" data={myDepartments.map((d) => ({ value: d.id, label: d.name }))} value={department} onChange={setDepartment} allowDeselect={false} />
        </Group>
        <SegmentedControl
          fullWidth
          value={legalEntity ?? ''}
          onChange={chooseLe}
          data={catalog.legalEntities.map((l) => ({ value: l.id, label: l.name }))}
        />
      </Stack>

      <Stack gap="md" p="md" style={{ flex: 1 }}>
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        {!legalEntity && (
          <Alert color="brand" variant="light">
            Выберите ООО, за которое вносите показатели.
          </Alert>
        )}
        {legalEntity && metrics === null && (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        )}
        {metrics?.length === 0 && !error && <Text c="dimmed">У подразделения нет показателей</Text>}
        {groups.map(([title, items]) => (
          <Box key={title} className="mis-card" px="md" py={6}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt={8} mb={2} style={{ letterSpacing: 0.6 }}>
              {title}
            </Text>
            {items.map((m, i) => (
              <Group key={m.id} justify="space-between" wrap="nowrap" mih={56} style={{ borderTop: i ? '1px solid #F0EEE9' : 0 }}>
                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                  {!m.editable && (
                    <Tooltip label={`${SOURCE_NAMES[m.source ?? 'manual']}: правится там`}>
                      <IconLock size={14} color="var(--mis-muted)" aria-label="Только просмотр" />
                    </Tooltip>
                  )}
                  <Text size="md">{m.name}</Text>
                </Group>
                <MetricInput m={m} value={values[m.id] ?? ''} onChange={(v) => setValues((x) => ({ ...x, [m.id]: v }))} />
              </Group>
            ))}
          </Box>
        ))}
      </Stack>

      <Stack gap={8} p="md" pb="lg" className="mis-sticky-footer">
        <Text size="sm" c="dimmed" ta="center">
          Если отправить ещё раз за этот день, значения заменятся, а не сложатся.
        </Text>
        <Button size="lg" onClick={submit} loading={busy} disabled={!legalEntity || !metrics?.some((m) => m.editable)}>
          {changed.length ? `Отправить (${changed.length})` : 'Отправить'}
        </Button>
      </Stack>
    </Box>
  );
}

function MetricInput({ m, value, onChange }: { m: DayMetric; value: number | ''; onChange: (v: number | '') => void }) {
  if (m.unit === 'count') {
    const n = value === '' ? 0 : value;
    return (
      <Group gap={6} wrap="nowrap">
        <ActionIcon size={44} variant="default" radius="md" disabled={!m.editable || n <= 0} onClick={() => onChange(Math.max(0, n - 1))} aria-label={`${m.name}: убавить`}>
          <IconMinus size={18} />
        </ActionIcon>
        <NumberInput
          aria-label={m.name}
          w={64}
          min={0}
          allowDecimal={false}
          hideControls
          disabled={!m.editable}
          value={value}
          onChange={(v) => onChange(v === '' ? '' : Number(v))}
          styles={{ input: { textAlign: 'center', fontWeight: 600, fontSize: 18 } }}
          inputMode="numeric"
        />
        <ActionIcon size={44} variant="light" radius="md" disabled={!m.editable} onClick={() => onChange(n + 1)} aria-label={`${m.name}: прибавить`}>
          <IconPlus size={18} />
        </ActionIcon>
      </Group>
    );
  }
  return (
    <NumberInput
      aria-label={m.name}
      w={150}
      min={0}
      decimalScale={2}
      thousandSeparator=" "
      decimalSeparator=","
      hideControls
      disabled={!m.editable}
      rightSection={<Text c="dimmed">{m.unit === 'rub' ? '₽' : '%'}</Text>}
      value={value}
      onChange={(v) => onChange(v === '' ? '' : Number(v))}
      styles={{ input: { textAlign: 'right', fontWeight: 600 } }}
      inputMode="decimal"
    />
  );
}
